/**
 * Created by Ella Ma on 02/08/2021.
 * Description:
 * ------ maintenance history ------
 * Changed by Daniel - Changed the style and logic of setting menu
 */

import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs/operators';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';

import { AppState } from '../../../redux';
import { SlideSheetActionTypes } from '../../../components/slide-sheet/slide-sheet.model';
import { Template, TemplateComponentConfiguration, TemplateComponentConfigurationActions } from '../../../components/template-configuration/template-configuration.model';
import { FieldConfig } from '../../../components/work-flow/field/field.model';
import { TaskDefinitionTemplateService } from '../../../components/work-flow/field/task-definition-template.service';
import { TaskDefinitionConfig, TaskDefinitionEventTypes, TaskDefinitionNeedValidControl } from '../../../components/work-flow/task-definition/task-definition.model';
import { PostActionConfig } from '../../../components/work-flow/task-post-action/task-post-action.model';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { Field, FieldBrief, FieldDefinition, Parent, PostNotification, TaskDefinition, TaskDefinitionOpType, TaskType } from '../../../tamalelibs/models/workflow.model';
import { WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { currentWorkflowFieldsSelector, workflowFieldsSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { Status, ValidStatus, WizardMenuConfig, WizardMenuEventType } from '../../../widgets/wizard-menu/wizard-menu.config';
import { BriefConfig } from '../../../widgets/widgets.model';
import { ControlType } from '../../../tamalelibs/models/template-control.model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { AlertBtnTypes } from '../../../widgets/alert-window/alert-window.model';
import { TaskPostActionComponent } from '../task-post-action/task-post-action.component';
import { action } from '@circlon/angular-tree-component/lib/mobx-angular/mobx-proxy';

@Component({
    selector: 'task-setting-wrapper',
    templateUrl: './task-setting-wrapper.component.html',
    styleUrls: ['./task-setting-wrapper.component.scss']
})
export class TaskSettingWrapperComponent implements OnInit, OnDestroy {

    @Input() config: TaskDefinitionConfig;
    @Input() opType: String;
    @Input() isImport: boolean;

    wizardConfig: WizardMenuConfig = new WizardMenuConfig();
    templateConfig: TemplateComponentConfiguration = new TemplateComponentConfiguration();
    postActionConfig = new PostActionConfig();
    taskDefinitionNeedValidControl = new TaskDefinitionNeedValidControl();
    taskDefinition: TaskDefinition = new TaskDefinition();

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _taskDefinitionTemplateService: TaskDefinitionTemplateService,
        private _storeQueriery: StoreQuerierService,
        private _alertWindow: AlertWindowService
    ) { }

    ngOnInit(): void {
        this._initSubscriptions();
        if (this.config.taskDefinition.type === TaskType.INPUT) {
            this._initTemplateConfig();
        }
        this._initPostActionConfig();
        this._initWizardData();

        this.taskDefinition = _.cloneDeep(this.config.taskDefinition);

        this.config.isImport = this.isImport;
        this.templateConfig.isImport = this.isImport;
        this.postActionConfig.isImport = this.isImport;
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    onCancel(): void {
        const isDataChanged = this.config.configChanged || this.templateConfig.configChanged || this.postActionConfig.configChanged;
        if (!isDataChanged) {
            this._cancelTakSettingPanel();
        } else {
            const subscription = this._alertWindow.warn('You have unsaved changes',
                ['Are you sure you want to leave this page?'], 'Leave', 'Stay').subscribe((result: boolean) => {
                    if (result) {
                        this._cancelTakSettingPanel();
                    }
                    subscription.unsubscribe();
                });
        }
    }

    onSave(): void {
        // task definition valid
        this.taskDefinitionNeedValidControl.name = true;
        this.taskDefinitionNeedValidControl.assignee = true;
        this.taskDefinitionNeedValidControl.stage = true;
        this.config.actionSubject$.next({ actiontype: TaskDefinitionEventTypes.VERIFY });
        // update the entity related fields to store
        if (this.templateConfig.template) {
            const taskDefinitionId = this.config.taskDefinition.id;
            const template = _.cloneDeep(this.templateConfig.template);
            this._updateCurrentWorkflowFieldsInStore(template, taskDefinitionId);
        }
        // update fieldDefID for the control which refer the entity field in current task
        this._updatePostActionParentFieldDefId();
        if (this._setWizardValidStatus()) {
            this._save();
        } else {
            const subscription = this._alertWindow.warn('You have incomplete settings',
                ['Do you want to save settings or complete the settings?'],
                'OK, I want to complete', 'Save', 'slideSheet', '', AlertBtnTypes.primary).subscribe((result: boolean) => {
                    if (!result) {
                        this._save();
                    }
                    subscription.unsubscribe();
                });
        }
    }

    private _cancelTakSettingPanel(): void {
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_USED_FIELD,
        });
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE,
        });
    }

    private _changeSideSheetMenu(active: number): void {
        const activeMenu = this.wizardConfig.config.menuData[this.wizardConfig.config.active];
        const valid = this._validForm(this.wizardConfig.config.active);
        activeMenu.validStatus = valid ? ValidStatus.validPass : ValidStatus.validFailed;
        activeMenu.status = Status.selected;
        this.wizardConfig.config.active = active;
    }

    private _changeSideSheetWidth(active) {
        // 1180 = 1132 + 48, 48 is wizard menu width
        const width = active === 1 && this.wizardConfig.config.menuData[active].id === 'form-builder' ? 1180 : 468;
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CHANGE_WIDTH,
            payload: width
        });
    }

    private _formatField(fieldIds: Array<string>, originalFields: Map<string, Field> = new Map()): Map<string, Field> {
        const fields: Map<string, Field> = new Map();
        this._storeQueriery.queryBySelector(workflowFieldsSelector).forEach((item) => {
            if (fieldIds.indexOf(item.id) > -1) {
                const one = new Field();
                one.id = item.id;
                one.fieldDefinition = item;
                if (originalFields.has(item.id)) {
                    one.value = originalFields.get(item.id).value;
                }
                fields.set(item.id, one);
            }
        });
        return fields;
    }

    private _initPostActionConfig() {
        if (this.config.taskDefinition.postAction) {
            const relationship = this.config.taskDefinition.postAction.relationship;
            if (this.isImport) {
                if (relationship && relationship.parent.controlId) {
                    this.postActionConfig.checkCreateRelationship = true;
                }
            } else {
                if (relationship &&
                    relationship.child &&
                    relationship.parent &&
                    relationship.type
                ) {
                    this.postActionConfig.checkCreateRelationship = true;
                }
            }
        }

        this.postActionConfig.isNotificationOn = PostNotification.isPostNotificationOn(this.config.taskDefinition.postNotification);
        if (this.postActionConfig.isNotificationOn) {
            TaskPostActionComponent.syncBetweenConfigAndFrmGroup(this.config.taskDefinition.postNotification, this.postActionConfig.notificationValidationFrmGroup);
        }
    }

    private _initSubscriptions() {
        this._destroySubscriptions.push(
            this.wizardConfig.events$.pipe(
                filter((item) => item.type === WizardMenuEventType.changeSelect)
            ).subscribe((item) => {
                this._changeSideSheetWidth(item.payload);
                this._changeSideSheetMenu(item.payload);
            }),
        );
    }

    private _initTemplateConfig() {
        this._store.pipe(
            select(workflowFieldsSelector),
            filter(data => data),
            take(1),
        ).subscribe(() => {
            let template: FieldConfig;
            let name: string;
            if (this.config.taskDefinition) {
                template = this.config.taskDefinition.template || {
                    id: this.config.taskDefinition.id,
                    width: 100,
                    contains: [],
                };
                name = this.config.taskDefinition.name;
            } else {
                template = new FieldConfig();
                template.width = 100;
                template.contains = [];
                name = this.config.taskDefinition.name;
            }
            this.templateConfig.title = 'Form Builder - ' + name;
            this.templateConfig.controls = [
                { name: 'Textbox', type: ControlType.TEXT, tid: 'text_box' },
                { name: 'Multi-line Textbox', type: ControlType.TEXT_AREA, tid: 'text_area' },
                { name: 'Text Dropdown', type: ControlType.SINGLE_TEXT_DROP_DOWN, tid: 'single_text_dropdown' },
                { name: 'Entity Dropdown', type: ControlType.SINGLE_ENTITY_DROP_DOWN, tid: 'single_entity_dropdown' },
                { name: 'Number', type: ControlType.NUMBER, tid: 'number' },
                { name: 'File', type: ControlType.FILE, tid: 'file_upload' },
                { name: 'Date', type: ControlType.DATE, tid: 'date' },
                { name: 'Rich Editor', type: ControlType.RICH_EDITOR, tid: 'rich_editor' },
                { name: 'Multi-Entity Textbox', type: ControlType.MULTI_ENTITY_DROP_DOWN, tid: 'multi_entity_textbox' },
                { name: 'Check List', type: ControlType.CHECK_LIST, tid: 'check_list' },
                { name: 'User Dropdown', type: ControlType.SINGLE_USER_DROP_DOWN, tid: 'single_user_dropdown' },
            ];
            this.templateConfig.service = this._taskDefinitionTemplateService;
            this.templateConfig.config = new BriefConfig();
            this.templateConfig.invalidControlId = this._taskDefinitionTemplateService.getInvalidControlId(template);
            this.templateConfig.invalidDetail = TaskDefinitionTemplateService.getInvalidControlDetail(template);
            this.templateConfig.template = template;
            this._taskDefinitionTemplateService.initializeTemplate(template).pipe(
                take(1),
            ).subscribe(res => {
                this.templateConfig.template = res;
            });
        });
    }

    private _initWizardData(): void {
        if (this.config.taskDefinition.type === TaskType.INPUT) {
            this.wizardConfig.config.menuData = [
                {
                    icon: 'detail-setting',
                    title: 'Basic Settings - ' + this.config.taskDefinition.name,
                    tid: 'basic',
                },
                {
                    id: 'form-builder',
                    icon: 'form-builder',
                    title: 'Form Builder - ' + this.config.taskDefinition.name,
                    tid: 'form_builder',
                },
                {
                    icon: 'post-actions',
                    title: 'Post Actions - ' + this.config.taskDefinition.name,
                    tid: 'post_actions',
                }];
        } else if (this.config.taskDefinition.type === TaskType.APPROVAL) {
            this.wizardConfig.config.menuData = [
                {
                    icon: 'dashboard',
                    title: 'Basic Settings - ' + this.config.taskDefinition.name,
                    tid: 'basic',
                },
                {
                    icon: 'post-actions',
                    title: 'Post Actions - ' + this.config.taskDefinition.name,
                    tid: 'post_actions',
                },
            ];
        }
        this.wizardConfig.config.active = 0;
        if (this.opType === TaskDefinitionOpType.EDIT) {
            this._setWizardValidStatus();
        }
    }

    private _updateCurrentWorkflowFieldsInStore(template: Template, taskDefinitionId: string): void {
        // update the entity related fields to store
        const currentFieldBriefs = [];
        const fieldsArrayForFullSet = [];
        const currentAllContrls = [];
        template.contains.forEach(row => {
            row.forEach((control: FieldConfig) => {
                const fldForUpdateStore = new FieldDefinition();
                fldForUpdateStore.id = control.fieldDefinitionId;
                // for less impact, just update name for this action.
                fldForUpdateStore.name = control.label;
                fieldsArrayForFullSet.push(fldForUpdateStore);
                if (control.type === ControlType.SINGLE_ENTITY_DROP_DOWN ||
                    control.type === ControlType.MULTI_ENTITY_DROP_DOWN ||
                    control.type === ControlType.SINGLE_USER_DROP_DOWN) {
                    const fieldBrief = FieldBrief.parse(control);
                    fieldBrief.taskDefinitionId = taskDefinitionId;
                    currentFieldBriefs.push(fieldBrief);
                }
                const ctrlCopy = { taskDefinitionId: taskDefinitionId };
                Object.assign(ctrlCopy, control);
                delete ctrlCopy['component'];
                delete ctrlCopy['configComponent'];
                delete ctrlCopy['config'];
                currentAllContrls.push(ctrlCopy);
            });
        });
        this._store.dispatch({
            type: WorkflowActionTypes.UPDATE_CURRENT_WORKFLOW_FIELDS,
            payload: {
                taskDefinitionId: taskDefinitionId,
                changedFields: currentFieldBriefs
            }
        });
        this._store.dispatch({
            type: WorkflowActionTypes.UPDATE_CURRENT_WORKFLOW_ALL_FIELDS,
            payload: {
                taskDefinitionId: taskDefinitionId,
                changedFields: currentAllContrls
            }
        });

        this._store.dispatch({
            type: WorkflowActionTypes.UPDATE_MULTIPLE_FIELDS,
            payload: fieldsArrayForFullSet
        });
    }

    private _save() {
        // handle template and fields if the task exists template
        if (this.templateConfig.template) {
            const template = _.cloneDeep(this.templateConfig.template);
            // preprocess template data before committing the saving.
            this._taskDefinitionTemplateService.preprocessTemplate(template);
            // assign data for taskDefinition.template
            const templateData = this._taskDefinitionTemplateService.formatTemplate(template);
            this.config.taskDefinition.template = templateData;
            // assign data for taskDefinition.fields
            const usedFieldIds = this._taskDefinitionTemplateService.formatFields();
            this.config.taskDefinition.fields = this._formatField(usedFieldIds);
        }
        // save task definition
        this._saveTaskDefinition();
    }

    private _saveTaskDefinition(): void {
        if (!this.taskDefinitionNeedValidControl.dynamicAssigneeFormGroup && this.config.taskDefinition.assignee &&
            this.config.taskDefinition.assignee.dependField) {
            // the dynamic assignee is off, empty corresponding field value.
            this.config.taskDefinition.assignee.dependField = null;
        }

        this.config.feedbackSubject$.next({
            type: TaskDefinitionEventTypes.SAVE,
            payload: {
                data: this.config.taskDefinition,
                valid: this.wizardConfig.config.menuData.every(item => item.validStatus === ValidStatus.validPass) && this._validSameLabelName()
            },
        });
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE,
        });
    }

    private _setWizardValidStatus() {
        if (this.config.taskDefinition.assignee.dependField && !this.config.taskDefinition.assignee.dependField.fieldDefId && !this.config.taskDefinition.assignee.dependField.relationshipType) {
            this.config.taskDefinition.assignee.dependField = null;
        }
        const isValidTaskDefination = this._validTaskDefinition();
        let isValidTemplate = this._validTemplate();
        const isValidPostAction = this._validPostAction();
        // TODO consider to remove this calling as the label conflict is able to check on Template Configruation Component right now.
        const isValidLabelName = this._validSameLabelName();
        if (isValidTaskDefination && !isValidTemplate && isValidPostAction && isValidLabelName) {
            // the case in which only template validation failed, in other words, no label duplication exists.
            if (this.templateConfig.invalidControlId.length > 0) {
                // only one invalid control found
                // obtain the validation failure detail.
                let isResultValid = true;
                for (let i = 0; i < this.templateConfig.invalidControlId.length; i++) {
                    const invalidId = this.templateConfig.invalidControlId[i];
                    const invalidDetail = this.templateConfig.invalidDetail.get(invalidId);
                    const isValid = !!invalidDetail && invalidDetail.isLableDuplicatedInvalid && !invalidDetail.isRequiredInvalid && !invalidDetail.isWidthInvalid;
                    if (!isValid) {
                        isResultValid = false;
                        break;
                    }
                }

                // if the only invalid control no other error except the label duplication error is found
                // we can say the template result is still valid, because the label duplication has been eliminated above.
                if (isResultValid) {
                    this.templateConfig.invalidControlId.splice(0);
                    this.templateConfig.invalidDetail.clear();
                    isValidTemplate = true;
                }
            }
        }

        this.wizardConfig.config.menuData[0].validStatus = isValidTaskDefination ? ValidStatus.validPass : ValidStatus.validFailed;
        if (this.wizardConfig.config.menuData[1].id === 'form-builder') {
            this.wizardConfig.config.menuData[1].validStatus = isValidTemplate && isValidLabelName ? ValidStatus.validPass : ValidStatus.validFailed;
        }
        this.wizardConfig.config.menuData[this.wizardConfig.config.menuData.length - 1].validStatus = isValidPostAction ? ValidStatus.validPass : ValidStatus.validFailed;

        if (isValidTaskDefination && isValidTemplate && isValidPostAction && isValidLabelName) {
            return true;
        } else {
            return false;
        }
    }

    private _updatePostActionParentFieldDefId(): void {
        // update fieldDefID for the control which refer the entity field in current task
        const postAction = this.config.taskDefinition.postAction;
        let isUpdated = false;
        if (postAction && postAction.relationship && postAction.relationship.parent) {
            const fieldBriefsInStore = this._storeQueriery.queryBySelector(currentWorkflowFieldsSelector);
            if (fieldBriefsInStore) {
                fieldBriefsInStore.forEach(field => {
                    if (field.controlId === postAction.relationship.parent.controlId) {
                        this.config.taskDefinition.postAction.relationship.parent.fieldDefId = field.fieldId;
                        isUpdated = true;
                    }
                });
            }

            if (!isUpdated) {
                this.config.taskDefinition.postAction.relationship.parent = new Parent();
            }
        }
    }

    private _validForm(index: number): boolean {
        if (index === 0) {
            return this._validTaskDefinition();
        }
        if (index === 1 && this.wizardConfig.config.menuData[1].id === 'form-builder') {
            return this._validTemplate();
        }
        if (index === this.wizardConfig.config.menuData.length - 1) {
            return this._validPostAction();
        }
        return false;
    }

    private _validPostAction(): boolean {
        let isRelationshipValid = true;
        if (this.postActionConfig.checkCreateRelationship) {
            const relationship = this.config.taskDefinition.postAction.relationship;
            isRelationshipValid = !!(relationship && relationship.child &&
                relationship.parent && relationship.parent.fieldDefId &&
                relationship.type);
        }

        const isNotificationValid = (this.postActionConfig.isNotificationOn && !this.postActionConfig.notificationValidationFrmGroup.invalid) || !this.postActionConfig.isNotificationOn;

        return isRelationshipValid && isNotificationValid;
    }

    private _validTaskDefinition(): boolean {
        const isDynamicOn = !!this.config.taskDefinition.assignee && !!this.config.taskDefinition.assignee.dependField && !!this.config.taskDefinition.assignee.dependField.fieldDefId;
        const isStaticOn = !!this.config.taskDefinition.assignee.teams || !!this.config.taskDefinition.assignee.users;
        if (!this.config.taskDefinition.name.trim() || this.config.taskDefinition.name.trim().length > 64) {
            return false;
        } else if (!isDynamicOn && !isStaticOn) {
            return false;
        } else if (!this.config.taskDefinition.stage || !this.config.taskDefinition.stage.id) {
            return false;
        } else if (isDynamicOn && (!this.config.taskDefinition.assignee.dependField
            || !this.config.taskDefinition.assignee.dependField.fieldDefId)) {
            return false;
        }

        return true;
    }

    private _validTemplate(): boolean {
        if (this.config.taskDefinition.type === TaskType.INPUT) {
            this.templateConfig.config.actionSubject$.next({
                type: TemplateComponentConfigurationActions.LEAVE_TEMPLATE_MENU
            });
            this.config.taskDefinition.template = this.templateConfig.template;
            if (!this.templateConfig.template || !this.templateConfig.template.contains.length) {
                return false;
            }
            return this.templateConfig.invalidControlId.length === 0;
        }
        return true;
    }

    private _validSameLabelName(): boolean {
        const res = new Map();
        if (this.templateConfig.template && this.templateConfig.template.contains) {
            let validSameLabelName = true;
            this.templateConfig.template.contains.forEach(item => {
                item.forEach(element => {
                    if (res.has(element.label)) {
                        validSameLabelName = false;
                    } else {
                        res.set(element.label, 1);
                    }
                });
            });
            return validSameLabelName;
        } else {
            return true;
        }
    }
}
